import React from "react";
import {
    Alert,
    Badge,
    Box,
    Button,
    Column,
    Columns,
    Dialog,
    Heading,
    IconRefresh,
    Inline,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
} from "braid-design-system";
import Loading from "../components/Loading";
import { useToast } from "../hooks/useToast";
import {
    AdjustmentProcessPreview,
    getAdjustmentProcessPreview,
    processAdjustment,
} from "../services/stockAdjustmentService";

const formatDateTime = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleString() : "-";

const formatQty = (value: number): string =>
    value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const varianceTone = (value: number): "positive" | "critical" | "secondary" =>
    value > 0 ? "positive" : value < 0 ? "critical" : "secondary";

const signed = (value: number, format: (v: number) => string): string =>
    value > 0 ? `+${format(value)}` : format(value);

const ProcessAdjustment: React.FC = () => {
    const toast = useToast();

    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [processing, setProcessing] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [preview, setPreview] = React.useState<AdjustmentProcessPreview | null>(null);
    const [searchTerm, setSearchTerm] = React.useState("");

    const loadData = React.useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setPreview(await getAdjustmentProcessPreview());
        } catch (error: any) {
            setLoadError(error?.message || "Failed to load adjustment preview");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const items = preview?.items ?? [];
    const filteredItems = React.useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return items;
        return items.filter(
            (item) =>
                item.item_code.toLowerCase().includes(term) ||
                (item.description || "").toLowerCase().includes(term) ||
                (item.group || "").toLowerCase().includes(term),
        );
    }, [items, searchTerm]);

    const totals = React.useMemo(
        () =>
            items.reduce(
                (acc, item) => ({
                    excess: acc.excess + (item.variance > 0 ? item.variance : 0),
                    shortage: acc.shortage + (item.variance < 0 ? -item.variance : 0),
                }),
                { excess: 0, shortage: 0 },
            ),
        [items],
    );

    const handleProcess = async () => {
        const initNo = preview?.open_init?.init_no;
        if (!initNo) return;
        setProcessing(true);
        try {
            const result = await processAdjustment(initNo);
            toast.showSuccess(
                `Stock adjustment ${result.init_no} processed - ${result.adjusted_lot_count} item lot(s) updated.`,
            );
            setConfirmOpen(false);
            await loadData();
        } catch (error: any) {
            toast.showError(error?.message || "Failed to process stock adjustment");
            setConfirmOpen(false);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return <Loading message="Please wait, loading adjustments..." />;
    }

    if (loadError) {
        return (
            <Box padding="large">
                <Text tone="critical">{loadError}</Text>
            </Box>
        );
    }

    const openInit = preview?.open_init ?? null;
    const notes = preview?.notes ?? [];

    return (
        <Box padding="medium">
            <Stack space="large">
                <Columns space="large" collapseBelow="tablet" alignY="center">
                    <Column>
                        <Inline space="small" alignY="center">
                            <Heading level="2">Process Adjustment</Heading>
                            {preview?.location ? <Badge tone="info">{`Location ${preview.location_name || preview.location}`}</Badge> : null}
                            {openInit ? <Badge tone="caution">{`Batch ${openInit.init_no}`}</Badge> : null}
                        </Inline>
                    </Column>
                    <Column width="content">
                        <Inline space="small" alignY="center">
                            <Button variant="ghost" tone="brandAccent" icon={<IconRefresh />} onClick={loadData}>
                                Refresh
                            </Button>
                            {openInit ? (
                                <Button
                                    variant="solid"
                                    tone="formAccent"
                                    loading={processing}
                                    onClick={items.length === 0 || processing ? undefined : () => setConfirmOpen(true)}
                                >
                                    Confirm Adjustment
                                </Button>
                            ) : null}
                        </Inline>
                    </Column>
                </Columns>

                {!openInit ? (
                    <Alert tone="info">
                        <Text>
                            There is no open stock initialization at this location. Start one from Stock
                            Initialization, enter Stock Adjustment notes, then confirm them here.
                        </Text>
                    </Alert>
                ) : (
                    <Stack space="large">
                        <Text tone="secondary">
                            {`Initialized ${formatDateTime(openInit.init_date)} - ${openInit.initialized_lot_count} item lots in groups ${openInit.group_codes.join(", ")}. `}
                            On confirmation each counted item's stock in hand becomes the counted quantity plus any
                            movement since initialization. Uncounted items are left unchanged.
                        </Text>

                        {preview && preview.draft_note_count > 0 ? (
                            <Alert tone="caution">
                                <Text>
                                    {`${preview.draft_note_count} Stock Adjustment note(s) are still saved as drafts. Drafts are not included - process them first if they belong to this count.`}
                                </Text>
                            </Alert>
                        ) : null}

                        <Stack space="small">
                            <Text weight="strong">{`Processed adjustment notes (${notes.length})`}</Text>
                            <Table label="Processed adjustment notes">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell><Text>Document No</Text></TableHeaderCell>
                                        <TableHeaderCell><Text>Date</Text></TableHeaderCell>
                                        <TableHeaderCell><Text>Created By</Text></TableHeaderCell>
                                        <TableHeaderCell align="right"><Text>Lines</Text></TableHeaderCell>
                                        <TableHeaderCell align="right"><Text>Total Qty</Text></TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {notes.length > 0 ? (
                                        notes.map((note) => (
                                            <TableRow key={note.doc_no}>
                                                <TableCell><Text weight="strong" tone="brandAccent">{note.doc_no}</Text></TableCell>
                                                <TableCell><Text size="small">{formatDateTime(note.date)}</Text></TableCell>
                                                <TableCell><Text size="small">{note.created_by || "-"}</Text></TableCell>
                                                <TableCell align="right"><Text>{note.line_count}</Text></TableCell>
                                                <TableCell align="right"><Text>{formatQty(note.total_qty)}</Text></TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colspan={5} align="center">
                                                <Text tone="secondary">
                                                    No adjustment notes have been processed against this batch yet.
                                                </Text>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Stack>

                        {items.length > 0 ? (
                            <Stack space="small">
                                <Columns space="medium" collapseBelow="tablet" alignY="center">
                                    <Column>
                                        <Inline space="small" alignY="center">
                                            <Text weight="strong">{`Counted items (${items.length})`}</Text>
                                            <Badge tone="positive">{`Excess ${formatQty(totals.excess)}`}</Badge>
                                            <Badge tone="critical">{`Shortage ${formatQty(totals.shortage)}`}</Badge>
                                        </Inline>
                                    </Column>
                                    <Column width="1/3">
                                        <TextField
                                            id="process-adjustment-search"
                                            aria-label="Search counted items"
                                            placeholder="Search counted items..."
                                            value={searchTerm}
                                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                                            onClear={() => setSearchTerm("")}
                                        />
                                    </Column>
                                </Columns>
                                <Table label="Counted items">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell><Text>Item</Text></TableHeaderCell>
                                            <TableHeaderCell><Text>Group</Text></TableHeaderCell>
                                            <TableHeaderCell align="right"><Text>SIH at Init</Text></TableHeaderCell>
                                            <TableHeaderCell align="right"><Text>Current SIH</Text></TableHeaderCell>
                                            <TableHeaderCell align="right"><Text>Counted</Text></TableHeaderCell>
                                            <TableHeaderCell align="right"><Text>Variance</Text></TableHeaderCell>
                                            <TableHeaderCell align="right"><Text>New SIH</Text></TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredItems.length > 0 ? (
                                            filteredItems.map((item) => (
                                                <TableRow key={`${item.item_code}-${item.lot || ""}`}>
                                                    <TableCell>
                                                        <Stack space="xxsmall">
                                                            <Text weight="strong">{item.item_code}</Text>
                                                            {item.description ? (
                                                                <Text size="small" tone="secondary">{item.description}</Text>
                                                            ) : null}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell><Text size="small">{item.group || "-"}</Text></TableCell>
                                                    <TableCell align="right"><Text>{formatQty(item.sih_image)}</Text></TableCell>
                                                    <TableCell align="right"><Text>{formatQty(item.current_sih)}</Text></TableCell>
                                                    <TableCell align="right"><Text weight="strong">{formatQty(item.counted)}</Text></TableCell>
                                                    <TableCell align="right">
                                                        <Text tone={varianceTone(item.variance)}>{signed(item.variance, formatQty)}</Text>
                                                    </TableCell>
                                                    <TableCell align="right"><Text weight="strong">{formatQty(item.new_sih)}</Text></TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colspan={7} align="center">
                                                    <Text tone="secondary">No matching items.</Text>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Stack>
                        ) : null}
                    </Stack>
                )}
            </Stack>

            <Dialog
                id="process-adjustment-confirm-dialog"
                title="Confirm stock adjustment?"
                open={confirmOpen}
                onClose={() => (processing ? undefined : setConfirmOpen(false))}
                width="small"
            >
                <Stack space="large">
                    <Text>
                        {`Stock in hand will be updated for ${items.length} counted item lot(s) from ${notes.length} note(s) in batch ${openInit?.init_no}, and the batch will be closed. This cannot be undone.`}
                    </Text>
                    <Inline space="small" align="right">
                        <Button variant="ghost" tone="neutral" onClick={processing ? undefined : () => setConfirmOpen(false)}>
                            Back
                        </Button>
                        <Button variant="solid" tone="formAccent" loading={processing} onClick={processing ? undefined : handleProcess}>
                            Confirm Adjustment
                        </Button>
                    </Inline>
                </Stack>
            </Dialog>
        </Box>
    );
};

export default ProcessAdjustment;
